<template>
<div class="modal-card">
  
  <header class="modal-card-head">
    <p v-if="volid" class="modal-card-title">Refine the date range for this Volume.</p>
    <p v-else class="modal-card-title">Choose which posts to include.</p>
  </header>
  
  <section class="modal-card-body" v-if="ready && selections.incCats">
    <div class="content">
      <b-field label="Include posts from this period...">
        <b-field class="control is-group">
            <b-datepicker placeholder="From..." icon="calendar-today" :read-only="false"
                          :years-range="[-50,20]"
                          :events="postsDateMarkers"
                          :date-formatter="dateFormatter"
                          v-on:changeMonth="onFromChange"
                          v-model="dateFromProxy">
            </b-datepicker>
            <span>&nbsp;&nbsp;to&nbsp;&nbsp;</span>
            <b-datepicker placeholder="...To" icon="calendar-today" :read-only="false" 
                          :years-range="[-50,20]"
                          :events="postsDateMarkers"
                          :date-formatter="dateFormatter"
                          v-model="dateToProxy">
            </b-datepicker>
        </b-field>
      </b-field>
      
      <b-message v-if="invalidDateRange" type="is-danger" has-icon>
        <div v-html="invalidDateRange"></div>
      </b-message>
      
      <b-field v-if="volid && availableCategories.length" label="">
        Note: This volume's included/excluded categories and individual
        post selections are inherited from the base project.
      </b-field>
      
      <b-field v-if="!volid && availableCategories.length" label="...that are in these categories...">
        <b-collapse class="card is-small" collapsible :open="false">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title" v-html="categoriesSummary"></p>
            <a class="card-header-icon"><b-icon :icon="props.open?'menu-up':'menu-down'"></b-icon></a>
          </div>
          <div class="card-content">
            <div class="columns">
              <div class="column">
                <b-field label="Categories Included">
                  <b-select expanded multiple native-size="6" v-model="selections.incCats">
                    <option selected value="__ALL__">Everything!!</option>
                    <option v-for="c in availableCategories" :value="c.name">
                      {{c.name}} ({{c.count}})</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column">
                <b-field label="Categories Excluded">
                  <b-select expanded multiple native-size="6" v-model="selections.excCats">
                    <option v-for="c in notIncludedCategories" :value="c">{{c}}</option>
                  </b-select>
                </b-field>
              </div>
            </div>
            <div size="is-small">hint: use <em>ctrl-click</em>
              to select multiple categories!</div>
          </div>
        </b-collapse>
      </b-field>
      <b-field v-if="!volid" label="..with individual posts added or removed:">
        <b-collapse class="card" :open="false">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title" v-html="postTuneSummary"></p>
            <a class="card-header-icon"><b-icon :icon="props.open?'menu-up':'menu-down'"></b-icon></a>
          </div>
          <div class="card-content">
          <!--b-input v-model="postFilter" placeholder="filter by..."></b-input-->
          <b-table :data="postsList"
                   data-key="pid"
                   checkable :checked-rows.sync="selectedPosts"
                   :default-sort="['date', 'asc']"
                   :paginated="true" :per-page="10"
                   style="margin: auto;">
            <b-table-column label="Date" field="date" sortable is-hidden-mobile v-slot="props">
              <span class="tag">{{props.row.date | formatDate}}</span>
            </b-table-column>
            <b-table-column label="Title" field="title" width="500" sortable v-slot="props">
              <a :href="props.row.link" target="_blank">{{ props.row.title }}</a>
            </b-table-column>
          </b-table>
          </div>
        </b-collapse>
      </b-field>
      <br>
      <b-field v-if="devMode">
        <b-collapse class="card dev-only" collapsible :open="false">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title">Dev Support Settings</p>
            <a class="card-header-icon"><b-icon :icon="props.open?'menu-up':'menu-down'"></b-icon></a>
          </div>
          <div class="card-content" style="width: 100%;">
            <b-checkbox v-model="reversePosts">Reverse posts</b-checkbox>
          </div>
        </b-collapse>
      </b-field>
    </div>
  </section>

  <footer class="modal-card-foot">
    <div class="level">
      <div class="level-left">
        {{selectedPosts.length}} posts selected.
      </div>
      <div class="level-right">
        <button class="button" @click="cancel">Cancel</button>
        <button class="button is-primary" @click="done" :disabled="invalidDateRange">Done</button>
      </div>
    </div>
  </footer>
</div>
</template>

<script>
import Defaults from '../defaults.js'
import Firebase from '../firebase.js'
var db = Firebase.database

const TODAY = new Date();
const ONE_YEAR_MS=365*24*3600*1000;
const ONE_YEAR_FUTURE = new Date(TODAY.getTime() + ONE_YEAR_MS)
const ONE_YEAR_AGO = new Date(TODAY.getTime() - ONE_YEAR_MS)
const FIVE_YEARS_AGO = new Date(TODAY.getTime() - ONE_YEAR_MS*5)
const TWENTY_YEARS_AGO = new Date(TODAY.getTime() - ONE_YEAR_MS*20)

export default {
  name: 'PostsConfigurator',
  data: function() { return {
    minDate: TWENTY_YEARS_AGO,
    maxDate: ONE_YEAR_FUTURE,
    posts: [],
    selections: {},
    initialSelection: undefined,
    postFilter: '',
    ready: 0,
    requiresInitialSelection: false,
    reversePosts: false,
  }},
  props: {
    projid: { type: String, required: true },
    volid: { type: String, default: undefined },
  },
  computed: {
    devMode: function() {
      return this.$store.state.dev.developmentClient
    },
    volume: function() {
      if (this.volid
          && this.selections
          && this.selections.volumes
          && this.selections.volumes[this.volid]) {
        return this.selections.volumes[this.volid]
      }
      return this.selections
    },
    dateFromProxy: {
      get() {
        if (!this.volume.dateFrom || isNaN(this.volume.dateFrom)) {
          this.volume.dateFrom = this.minDate.getTime()/1000
        }
        return new Date(this.volume.dateFrom*1000)
      },
      set(d) {
        d.setHours(0,0,0,0)
        this.volume.dateFrom = d.getTime()/1000
      }
    },
    dateToProxy: {
      get() {
        if (!this.volume.dateTo || isNaN(this.volume.dateTo)) {
          this.volume.dateTo = this.maxDate.getTime()/1000;
        }
        return new Date(this.volume.dateTo*1000)
      },
      set(d) {
        d.setHours(23,59,59)
        this.volume.dateTo = d.getTime()/1000
      },
    },
    invalidDateRange: function() {
      if (this.dateToProxy < this.dateFromProxy) {
        return '<em>From</em> date must be before <em>To</em> date!'
      }
      return undefined
    },
    availableCategories: function() {
      var cats = {};
      this.postsFilteredByDate.forEach( p => {
        if (p.categories) {
          p.categories.forEach( c => cats[c] = (cats[c]||0) + 1)
        }
      })
      var numberedcats = Object.keys(cats)
          .sort((a,b) => cats[b] - cats[a])
          .map((c) => { return { name: c, count: cats[c] } })
      return numberedcats;
    },
    notIncludedCategories: function() {
      var catin = this.selections.incCats;
      var allin = catin.indexOf('__ALL__') >= 0
      var excats = new Array()
      this.postsFilteredByDate.forEach( p => {
        var pcats = p.categories
        for (var c in pcats) {
          if (allin || catin.indexOf(pcats[c]) >= 0) {
            excats = excats.concat(pcats);
            break;
          }
        }
      })
      return excats.filter((c,i) => excats.indexOf(c)==i && catin.indexOf(c)<0)
    },
    postsList: function() {
      console.log('postsList len=', Object.keys(this.posts).length)
      var posts = this.posts
      return Object.keys(posts)
        .filter(k => k!='.key')
        .filter(k => !this.postFilter || posts[k].title.indexOf(this.postFilter)>=0)
        .map(k=>posts[k])
    },
    postsDateMarkers: function() {
      return Object.keys(this.postsList)
        .map( k => new Date(new Date(this.postsList[k].date*1000).setHours(0,0,0,0)) )
    },
    selectedPosts: {
      get: function() {
        var posts = this.postsList
        var selected = this.postsFilteredByDateAndCategories
        var inc = this.selections.incPosts || []
        var exc = this.selections.excPosts || []
        if (posts) {
          posts.forEach(p => inc.indexOf(p.pid) >= 0 && selected.indexOf(p) < 0 && selected.push(p))
          selected = selected.filter(p => exc.indexOf(p.pid) < 0)
        }
        selected.sort((a,b)=>{
          if (a.date != b.date) {
            return a.date - b.date
          } else if (a.sequence !== undefined && b.sequence !== undefined) {
            return b.sequence - a.sequence // sequence indicates age!
          } else {
            return 0
          }
        })
        console.log('GET selectedPosts', selected.map(p => p.sequence))
        return selected
      },
      set: function(newVal) {
        console.log('SET selectedPosts', newVal)
        var catsels = this.postsFilteredByDateAndCategories
        var adds = newVal.filter(p => catsels.indexOf(p) < 0)
        var removes = catsels.filter(p => newVal.indexOf(p) < 0)
        this.$set(this.selections, 'incPosts', adds.map(p => p.pid))
        this.$set(this.selections, 'excPosts', removes.map(p => p.pid))
      }
    },
    postsFilteredByDate: function() {
      var dateFrom = this.dateFromProxy.getTime()/1000
      var dateTo = this.dateToProxy.getTime()/1000
      if (this.postsList) {
        var selected = this.postsList.filter(function(p) {
          if (!p || typeof(p) != 'object') {
            return false
          } else if (p.date && (p.date < dateFrom || p.date > dateTo) ) {
            return false
          }
          return true
        })
        console.log('GET postsFilteredByDate', selected)
        return selected
      }
      return []
    },
    postsFilteredByDateAndCategories: function() {
      var catex = this.selections.excCats || []
      var catin = this.selections.incCats || []
      var selected = this.postsFilteredByDate.filter(function(p) {
        if (p.categories) {
          var allow = catin.indexOf('__ALL__')>=0
              || p.categories.some(function(c){ return catin.indexOf(c)>=0 })
          var reject = p.categories.some(function(c){ return catex.indexOf(c)>=0 })
          return allow && !reject
        } else {
          return catin.indexOf('__ALL__') >= 0
        }
      })
      //console.log('GET postsFilteredByDateAndCategories', selected)
      return selected
    },
    categoriesSummary: function() {
      var summary = ""
      var incCats = this.selections.incCats.filter(c=> c!='__ALL__')
      var excCats = this.selections.excCats.filter(c => c!='__NONE__')
      if (incCats.length==0) {
        summary = "Everything";
      } else {
        summary = "Include&nbsp;<i>" + incCats.join(", ")+"</i>";
      }
      if (excCats.length>0) {
        summary += ", except&nbsp;<i>" + excCats.join(", ")+"</i>";
      }
      return summary;
    },
    postTuneSummary: function() {
      var summary = "<i>&nbsp;&nbsp;("
      var incPosts = this.selections.incPosts || []
      var excPosts = this.selections.excPosts || []
      var filtPosts = this.postsFilteredByDateAndCategories
      incPosts = incPosts.filter(p=>filtPosts.indexOf(p)>=0)
      excPosts = excPosts.filter(p=>filtPosts.indexOf(p)>=0)
      
      if (incPosts.length) {
        summary += "&nbsp;add "+incPosts.length;
      }
      if (excPosts.length) {
        if (incPosts.length) { summary += ' and' }
        summary += "&nbsp; remove "+excPosts.length;
      }
      else if (incPosts.length==0) {
        summary += "&nbsp;no additions or removals"
      }
      return summary+"&nbsp;)</i>"
    },
    datesSummary: function() {
      return this.dateFromProxy.toDateString().slice(4) + " to " +
        this.dateToProxy.toDateString().slice(4)
    },
  },
  methods: {
    dateFormatter: function(date) {
      return date.toDateString().slice(4)
    },
    onFromChange: function(what) {
      console.log("onFromChange", what)
    },
    cancel: function() {
      this.$parent.close()
    },
    done: function() {
      if (this.volid) {
        var update = Object.assign({}, this.volume)
        update.configuredPosts = true
        update.selectedPosts = this.selectedPosts.map(p => p.pid)
        if (this.reversePosts) {
          update.selectedPosts = update.selectedPosts.reverse()
        }
        delete update['.key']
        console.log('updating volume selections:', update)
        this.$firebaseRefs.selections.child('volumes').child(this.volid).update(update)
      } else {
        var update = Object.assign({}, this.selections)
        update.configuredPosts = true
        update.selectedPosts = this.selectedPosts.map(p => p.pid) 
        if (this.reversePosts) {
          update.selectedPosts = update.selectedPosts.reverse()
        }
        delete update['.key']
        console.log('updating project selections:', update)
        this.$firebaseRefs.selections.update(update)
      }

      this.$parent.close()
    },
  },
  watch: {
    ready: function(now, was) {
      console.log("ready:", now, was)
      if (now >= 3) {
        var sels = this.selections
        var defvals = { format: Defaults.book.format,
                        incCats: ['__ALL__'],
                        excCats: ['__NONE__'],
                        dateFrom: ONE_YEAR_AGO,
                        dateTo: ONE_YEAR_FUTURE,
                        coverShowTitlePage: true }
        var update = Object.assign(defvals, sels)
        delete update['.key']
        
        console.log("update selections:", update, sels)
        this.$firebaseRefs.selections.update(update)

        if (!sels.selectedPosts || sels.selectedPosts.length==0) {
          console.log("requiresInitialSelection TRUE", this.selectedPosts)
          this.$firebaseRefs.selections.update({selectedPosts: this.selectedPosts.map(p => p.pid)})
          this.requiresInitialSelection = true
        }
      }
    },
  },
  beforeMount: function() {
    console.log('selections', this.$store.state.uid)
    var selpath = "/projects/"+this.$store.state.uid+"/"+this.projid+"/selections"
    var postspath = "/projects/"+this.$store.state.uid+"/"+this.projid+"/posts"
    
    this.$bindAsObject("selections", db.ref(selpath), null, ()=>this.ready += 1)
    this.$bindAsObject("posts", db.ref(postspath), null, ()=>this.ready += 2)
  }
}
</script>


<style scoped>
.level {
    width: 100%;
}
.button {
    min-width: 8em;
}
</style>



// WEBPACK FOOTER //
// src/components/PostsConfigurator.vue